import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import { TLocation } from 'src/types/others'
import { EPatientGender } from 'src/types/patient'

export const getStringWithFirstLetterInUpperCase = (stringToUpperCase: string) => {
  return `${stringToUpperCase.slice(0, 1).toUpperCase()}${stringToUpperCase.slice(1)}`
}

const getFullName = (person?: { firstname?: string; lastname?: string }) => {
  const firstname = person?.firstname?.toLowerCase() || ''
  const lastNameInUpperCase = person?.lastname?.toUpperCase() || ''
  return `${lastNameInUpperCase} ${getStringWithFirstLetterInUpperCase(firstname)}`
}

const getPatientCivility = (t: any, patient: { gender: EPatientGender }) => {
  if (patient.gender === EPatientGender.FEMALE)
    return t('components.patient.patient-file.header.patient-infos.civility-mrs')
  if (patient.gender === EPatientGender.MALE)
    return t('components.patient.patient-file.header.patient-infos.civility-mr')
  return ''
}

const getPatientCivilityAndFullName = (
  t: any,
  patient: { gender: EPatientGender; firstname?: string; lastname?: string },
) => {
  const civility = getPatientCivility(t, patient)
  const fullName = getFullName(patient)
  return [civility, fullName].filter(Boolean).join(' ')
}

const getDoctorCivilityAndName = (
  medicalStaffMember?: { civility?: string; firstname?: string; lastname?: string } | null,
  withFullName = true,
) => {
  const civility = medicalStaffMember?.civility
  const lastnameUppercase = medicalStaffMember?.lastname?.toUpperCase()

  if (!medicalStaffMember?.firstname) {
    return [civility, lastnameUppercase].filter(Boolean).join(' ')
  }

  const firstname = withFullName
    ? capitalizeFirstname(medicalStaffMember.firstname)
    : `${medicalStaffMember.firstname?.charAt(0)?.toUpperCase()}.`
  return [civility, lastnameUppercase, firstname].filter(Boolean).join(' ')
}

const getDoctorName = (medicalStaffMember?: TMedicalStaffMember | null) => {
  if (!medicalStaffMember?.firstname) return medicalStaffMember?.lastname?.toUpperCase() || ''
  if (!medicalStaffMember?.lastname) return getStringWithFirstLetterInUpperCase(medicalStaffMember?.firstname)
  return `${medicalStaffMember?.lastname?.toUpperCase()} ${getStringWithFirstLetterInUpperCase(
    medicalStaffMember?.firstname,
  )}`
}

const capitalizeFirstname = (firstname: string) => {
  const capitalizedFirstname = firstname.trim().replace(/[A-zÀ-ú]+/g, function (word) {
    return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase()
  })
  return capitalizedFirstname
}

const capitalizeFirstLetter = (givenString: string) => {
  return givenString.charAt(0).toUpperCase() + givenString.slice(1)
}

const getFirstThreeWords = (str: string) => {
  const cleanedStr = str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  const words = cleanedStr.match(/\b\w+\b/g)
  if (!words) return ''
  if (words.length === 1) return words[0]
  if (words.length === 2) return `${words[0]}-${words[1]}`
  return `${words[0]}-${words[1]}-${words[2]}`
}

const getFullAddress = (location: TLocation) => {
  return [location.name, location.number, location.street, location.zipcode, location.city?.toUpperCase()]
    .filter(Boolean)
    .join(' ')
}

export {
  getFullName,
  getPatientCivility,
  getPatientCivilityAndFullName,
  getDoctorCivilityAndName,
  getDoctorName,
  capitalizeFirstname,
  capitalizeFirstLetter,
  getFirstThreeWords,
  getFullAddress,
}
